/**
 * PB-AIT-2/3 — AI Text pure logic (settings, readiness, regen cap, cart encode).
 */

import { lookupSelectionValue } from '@theme/pb-builder-tokens';

export const DEBOUNCE_MS = 400;
export const DEFAULT_MAX_CHARS = 400;
export const DEFAULT_MAX_REGENERATES = 5;
export const DEFAULT_LOADING_MESSAGE = 'Creating your text…';
export const AI_TEXT_LAYOUT_CONTENT_ONLY = 'content-only';
export const AI_TEXT_LAYOUT_CONTROLS_VISIBLE = 'controls-visible';

function splitDependsOn(value) {
  return String(value || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Normalize Shopify metaobject type values (string, object, or list choice). */
export function extractFieldTypeLabel(field) {
  const raw = field?.type;
  if (raw == null) return '';
  if (typeof raw === 'string') return raw.trim();
  if (Array.isArray(raw) && raw.length) return extractFieldTypeLabel({ type: raw[0] });
  if (typeof raw === 'object') {
    const candidate = raw.value ?? raw.label ?? raw.name ?? raw.title ?? '';
    return String(candidate).trim();
  }
  return String(raw).trim();
}

function normalizeFieldTypeKey(field) {
  return extractFieldTypeLabel(field).toLowerCase().replace(/[\s_-]+/g, ' ').trim();
}

/** True when PB field type is AI Text (supports common Shopify choice spellings). */
export function isAiTextFieldType(field) {
  const key = normalizeFieldTypeKey(field);
  if (!key) return false;
  if (key.includes('ai text')) return true;
  if (key === 'aitext') return true;
  return false;
}

/** Settings block looks like AI Text (depends_on + prompt) regardless of declared type. */
export function hasAiTextSettingsSignature(field) {
  const cfg = parseAiTextFieldSettings(field?.settings);
  return cfg.depends_on.length > 0 && cfg.prompt.length > 0;
}

/** Legacy misconfig flag — settings signature now routes to AI Text adapter instead of blocking. */
export function isAiTextFieldMisconfigured(field) {
  return false;
}

function parseBool(val, defaultVal = false) {
  if (val == null || val === '') return defaultVal;
  return val === true || ['true', 'on', '1'].includes(String(val).toLowerCase());
}

function selectionValue(selections, label, scopeProductId) {
  if (!selections || label == null) return '';
  /* PB-SBT-1 — shared seam: resolves bundle-scoped keys, plain lookup elsewhere. */
  const val = lookupSelectionValue(selections, label, { scopeProductId });
  if (val == null) return '';
  if (typeof val === 'object' && !Array.isArray(val)) {
    const keys = Object.keys(val);
    for (let i = 0; i < keys.length; i += 1) {
      const nested = val[keys[i]];
      if (nested != null && String(nested).trim()) return String(nested).trim();
    }
    return '';
  }
  return String(val).trim();
}

/** Parse AI Text field settings from BUILDER_DATA field config. */
export function parseAiTextFieldSettings(raw) {
  const defaults = {
    depends_on: [],
    prompt: '',
    editable: false,
    required: false,
    review_required: false,
    max_regenerates: DEFAULT_MAX_REGENERATES,
    max_chars: DEFAULT_MAX_CHARS,
    model: '',
    background_image: '',
    output_property: '',
    include_metadata: false,
    allow_regenerate: true,
    loading_message: '',
  };
  if (raw == null) return { ...defaults };

  if (typeof raw === 'object' && !Array.isArray(raw)) {
    if (typeof raw.value === 'string' && raw.value.trim()) {
      return { ...defaults, ...parseAiTextFieldSettings(raw.value) };
    }
    const out = { ...defaults };
    if (raw.depends_on != null) out.depends_on = splitDependsOn(raw.depends_on);
    if (raw.prompt != null) out.prompt = String(raw.prompt).trim();
    if (raw.editable != null) out.editable = parseBool(raw.editable, false);
    if (raw.required != null) out.required = parseBool(raw.required, false);
    if (raw.review_required != null) out.review_required = parseBool(raw.review_required, false);
    if (raw.max_regenerates != null) {
      out.max_regenerates = parseInt(raw.max_regenerates, 10) || DEFAULT_MAX_REGENERATES;
    }
    if (raw.max_chars != null) out.max_chars = parseInt(raw.max_chars, 10) || DEFAULT_MAX_CHARS;
    if (raw.model != null) out.model = String(raw.model).trim();
    if (raw.background_image != null) out.background_image = String(raw.background_image).trim();
    if (raw.output_property != null) out.output_property = String(raw.output_property).trim();
    if (raw.include_metadata != null) out.include_metadata = parseBool(raw.include_metadata, false);
    if (raw.allow_regenerate != null) out.allow_regenerate = parseBool(raw.allow_regenerate, true);
    if (raw.loading_message != null) out.loading_message = String(raw.loading_message).trim();
    return out;
  }

  const text = String(raw).trim();
  if (!text) return { ...defaults };
  if (text.charAt(0) === '{') {
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === 'object') return parseAiTextFieldSettings(parsed);
    } catch (e) {
      /* fall through */
    }
  }

  const out = { ...defaults };
  const lines = text.indexOf('\n') >= 0 ? text.split(/\r?\n/) : [text];
  lines.forEach((line) => {
    line = line.trim();
    if (!line || line.indexOf(':') === -1) return;
    const idx = line.indexOf(':');
    const key = line.slice(0, idx).trim().toLowerCase().replace(/\s+/g, '_');
    const val = line.slice(idx + 1).trim();
    if (key === 'depends_on') out.depends_on = splitDependsOn(val);
    else if (key === 'prompt') out.prompt = val;
    else if (key === 'editable') out.editable = parseBool(val, false);
    else if (key === 'required') out.required = parseBool(val, false);
    else if (key === 'review_required') out.review_required = parseBool(val, false);
    else if (key === 'max_regenerates') out.max_regenerates = parseInt(val, 10) || DEFAULT_MAX_REGENERATES;
    else if (key === 'max_chars') out.max_chars = parseInt(val, 10) || DEFAULT_MAX_CHARS;
    else if (key === 'model') out.model = val;
    else if (key === 'background_image') out.background_image = val;
    else if (key === 'output_property') out.output_property = val;
    else if (key === 'include_metadata') out.include_metadata = parseBool(val, false);
    else if (key === 'allow_regenerate') out.allow_regenerate = parseBool(val, true);
    else if (key === 'loading_message') out.loading_message = val;
  });
  return out;
}

/** Resolve `{Label}` tokens from builder selections (same rules as prompt / question tokens). */
export function resolveAiTextTokens(template, selections) {
  const str = String(template ?? '');
  if (!str.includes('{')) return str;
  return str.replace(/\{([^}]+)\}/g, (match, label) => {
    const key = String(label || '').trim();
    if (!key) return '';
    return selectionValue(selections, key);
  });
}

/** Pure layout mode from parsed AI Text settings (UX PRD U1, U7). */
export function resolveAiTextLayoutMode(cfg) {
  const settings = cfg || {};
  const controlsVisible =
    settings.allow_regenerate !== false || !!settings.editable || !!settings.review_required;
  return controlsVisible ? AI_TEXT_LAYOUT_CONTROLS_VISIBLE : AI_TEXT_LAYOUT_CONTENT_ONLY;
}

/** Preview loader copy — blank loading_message falls back to platform default. */
export function resolveLoadingMessage(cfg, selections) {
  const settings = cfg || {};
  const template = settings.loading_message != null ? String(settings.loading_message).trim() : '';
  if (!template) return DEFAULT_LOADING_MESSAGE;
  const resolved = resolveAiTextTokens(template, selections).trim();
  return resolved || DEFAULT_LOADING_MESSAGE;
}

/** Build orderProps from depends_on labels + current selections. */
export function buildOrderProps(dependsOnLabels, selections, scopeProductId) {
  const labels = Array.isArray(dependsOnLabels) ? dependsOnLabels : [];
  const orderProps = {};
  for (let i = 0; i < labels.length; i += 1) {
    const label = labels[i];
    const customerVal = selectionValue(selections, label, scopeProductId);
    if (!customerVal) return { complete: false, orderProps: null };
    orderProps[label] = customerVal;
  }
  return { complete: true, orderProps };
}

/** Stable fingerprint for dependency values (Design Library orderPropsKey shape). */
export function buildDependencyFingerprint(orderProps) {
  const keys = Object.keys(orderProps || {}).sort();
  return keys.map((k) => `${k}=${orderProps[k]}`).join('|');
}

export function shouldResetOnFingerprintChange(previousKey, nextKey) {
  if (!previousKey || !nextKey) return false;
  return previousKey !== nextKey;
}

export function isTextJobActivelyRunning(state) {
  if (!state) return false;
  if (state.isGenerating) return true;
  const status = state.jobStatus || 'idle';
  return status === 'queued' || status === 'running';
}

export function isTextJobComplete(state) {
  if (!state) return false;
  const status = state.jobStatus || 'idle';
  const text = state.generatedText != null ? String(state.generatedText).trim() : '';
  return status === 'complete' && text.length > 0;
}

/** Resolve cart line property key for final AI text. */
export function resolveAiTextOutputPropertyKey(cfg, fieldLabel) {
  const settings = cfg || {};
  const custom = settings.output_property != null ? String(settings.output_property).trim() : '';
  if (custom) return custom;
  return fieldLabel != null ? String(fieldLabel).trim() : '';
}

/** Hidden metadata key when include_metadata is enabled. */
export function buildAiTextMetadataPropertyKey(outputKey) {
  const base = outputKey != null ? String(outputKey).trim() : '';
  if (!base) return '';
  return `_${base}_ai_meta`;
}

// Pilot only: unwrap provider writing artifacts without rewriting the actual prose.
export function normalizeGeneratedNameMeaning(text, productId, outputKey) {
  const value = String(text ?? '');
  if (String(productId) !== '15272707817776' || outputKey !== 'Name Meaning') return value;
  const opening = /^\s*:::writing(?:\{[^\r\n]*\})?[ \t]*(?:\r?\n|$)/;
  if (!opening.test(value)) return value;
  return value.replace(opening, '').replace(/(?:\r?\n)?:::[ \t]*\s*$/, '').trim();
}

/** Final text shown to customer (edited override when editable). */
export function resolveAiTextFinalValue(state) {
  if (!state) return '';
  const edited = state.editedText != null ? String(state.editedText).trim() : '';
  if (edited) return edited;
  return state.generatedText != null ? String(state.generatedText).trim() : '';
}

/**
 * Stable id for review ack — session-scoped (regen clears via fingerprint change).
 * @param {object|null|undefined} state
 */
export function buildTextReviewFingerprint(state) {
  if (!state || typeof state !== 'object') return '';
  const text = resolveAiTextFinalValue(state);
  if (!text) return '';
  const jobId = state.jobId != null ? String(state.jobId).trim() : '';
  const regen = String(state.regenerateCount == null ? 0 : state.regenerateCount);
  return [text, jobId, regen].join('\u001f');
}

export function isRegenerateCapReached(state, cfg) {
  const settings = cfg || {};
  const cap = parseInt(settings.max_regenerates, 10);
  const maxRegens = Number.isFinite(cap) && cap > 0 ? cap : DEFAULT_MAX_REGENERATES;
  const used = state && state.regenerateCount != null ? Number(state.regenerateCount) : 0;
  return used >= maxRegens;
}

/** True when Generate again is allowed (text ready, not running, under cap). */
export function canRegenerateText(state, cfg) {
  const settings = cfg || {};
  if (settings.allow_regenerate === false) return false;
  if (!isTextJobComplete(state)) return false;
  if (isTextJobActivelyRunning(state)) return false;
  return !isRegenerateCapReached(state, cfg);
}

/**
 * Readiness contract — stepNavigable vs finalizeReady (align with AI Image).
 * @param {object|null|undefined} state
 * @param {object|null|undefined} cfg — parsed settings
 * @param {{ reviewAcknowledged?: boolean } | null} [options]
 */
export function computeAiTextReadiness(state, cfg, options) {
  const settings = cfg || {};
  const required = !!settings.required;
  const reviewRequired = !!settings.review_required;
  const text = resolveAiTextFinalValue(state);
  const hasText = text.length > 0;
  const running = isTextJobActivelyRunning(state);
  const failed = (state && state.jobStatus === 'failed') || !!(state && state.aiError);
  const jobStatus = (state && state.jobStatus) || 'idle';
  const reviewAcknowledged = !!(options && options.reviewAcknowledged);
  const textReady = hasText && jobStatus === 'complete' && !running && !failed;

  // Optional enrichment must never become a purchase requirement because a job
  // started, failed, or was restored. Output eligibility is checked separately.
  const stepNavigable = !required || textReady;
  const finalizeReady = !required || (textReady && (!reviewRequired || reviewAcknowledged));

  return {
    stepNavigable,
    finalizeReady,
    jobStatus,
    textReady,
    required,
    reviewRequired,
    regenerateCapReached: isRegenerateCapReached(state, settings),
    canRegenerate: canRegenerateText(state, settings),
  };
}

/**
 * Encode AI Text field into tier-1 cart properties.
 * @param {object} field — personalization field config
 * @param {object|null|undefined} state — runtime field state
 * @param {Record<string, unknown>} [selections] — builder selections fallback
 */
export function encodeAiTextFieldProps(field, state, selections, productId) {
  const props = {};
  if (!field || !field.label) return props;
  const cfg = parseAiTextFieldSettings(field.settings);
  const outputKey = resolveAiTextOutputPropertyKey(cfg, field.label);
  if (!outputKey) return props;

  // Draft snapshots intentionally omit job IDs/status; an explicit non-complete
  // status must never be upgraded to success or replaced with a selection fallback.
  const snapshot = state && !state.jobStatus ? { ...state, jobStatus: 'complete' } : state;
  if (!computeAiTextReadiness(snapshot, cfg).textReady) return props;
  if (cfg.review_required && !state?.reviewAcked) return props;
  const gate = buildOrderProps(cfg.depends_on, selections, field.provenance?.productId);
  if (!gate.complete) return props;
  if (cfg.depends_on.length && state.dependencyFingerprint !== buildDependencyFingerprint(gate.orderProps)) {
    return props;
  }
  const text = resolveAiTextFinalValue({ ...state,
    generatedText: normalizeGeneratedNameMeaning(state.generatedText, productId, outputKey),
  });
  if (!text) return props;

  props[outputKey] = text;
  if (cfg.include_metadata) {
    const metaKey = buildAiTextMetadataPropertyKey(outputKey);
    if (metaKey) {
      props[metaKey] = JSON.stringify({
        jobId: state && state.jobId ? String(state.jobId) : '',
        model: state && state.model ? String(state.model) : cfg.model || '',
      });
    }
  }
  return props;
}

/** Collect allowed tier-1 keys for AI Text fields (output + optional metadata). */
export function collectAiTextAllowedKeys(fieldConfig) {
  const allowed = new Set();
  if (!Array.isArray(fieldConfig)) return allowed;
  fieldConfig.forEach((field) => {
    if (!field || !field.label) return;
    if (!String(field.type || '').toLowerCase().includes('ai text')) return;
    const cfg = parseAiTextFieldSettings(field.settings);
    const outputKey = resolveAiTextOutputPropertyKey(cfg, field.label);
    if (outputKey) allowed.add(outputKey);
    if (cfg.include_metadata) {
      const metaKey = buildAiTextMetadataPropertyKey(outputKey);
      if (metaKey) allowed.add(metaKey);
    }
  });
  return allowed;
}

/** Whether to enqueue background prefetch for this field state. */
export function shouldScheduleTextPrefetch(state, gate, fingerprintKey) {
  if (!gate || !gate.complete || !fingerprintKey) return false;
  if (!state) return true;
  if (state.dependencyFingerprint && state.dependencyFingerprint !== fingerprintKey) return true;
  if ((state.jobStatus === 'skipped' || state.jobStatus === 'failed') && state.dependencyFingerprint === fingerprintKey) return false;
  if (isTextJobActivelyRunning(state)) return false;
  if (isTextJobComplete(state) && state.dependencyFingerprint === fingerprintKey) return false;
  return true;
}

export function createFreshTextFieldState() {
  return {
    generatedText: '',
    editedText: '',
    jobId: '',
    jobStatus: 'idle',
    dependencyFingerprint: '',
    isGenerating: false,
    aiError: '',
    prefetchedReady: false,
    regenerateCount: 0,
    model: '',
  };
}

if (typeof globalThis !== 'undefined') {
  globalThis.PBAiTextLogic = {
    DEBOUNCE_MS,
    DEFAULT_MAX_CHARS,
    DEFAULT_MAX_REGENERATES,
    DEFAULT_LOADING_MESSAGE,
    AI_TEXT_LAYOUT_CONTENT_ONLY,
    AI_TEXT_LAYOUT_CONTROLS_VISIBLE,
    parseAiTextFieldSettings,
    resolveAiTextTokens,
    resolveAiTextLayoutMode,
    resolveLoadingMessage,
    extractFieldTypeLabel,
    isAiTextFieldType,
    hasAiTextSettingsSignature,
    isAiTextFieldMisconfigured,
    buildOrderProps,
    buildDependencyFingerprint,
    shouldResetOnFingerprintChange,
    isTextJobActivelyRunning,
    isTextJobComplete,
    computeAiTextReadiness,
    shouldScheduleTextPrefetch,
    createFreshTextFieldState,
    resolveAiTextOutputPropertyKey,
    buildAiTextMetadataPropertyKey,
    resolveAiTextFinalValue,
    buildTextReviewFingerprint,
    isRegenerateCapReached,
    canRegenerateText,
    encodeAiTextFieldProps,
    collectAiTextAllowedKeys,
  };
}

// Remember outages briefly, not forever. Expiry is evaluated on entry, never by a retry timer.
export const AI_TEXT_FAILURE_COOLDOWN_MS = 5 * 60 * 1000;
export function isAiTextFailureCoolingDown(state, now = Date.now()) {
  const failedAt = Number(state?.failedAt);
  return state?.jobStatus === 'failed' && Number.isFinite(failedAt) && failedAt > 0 &&
    now >= failedAt && now - failedAt < AI_TEXT_FAILURE_COOLDOWN_MS;
}
